import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import ReservationActionsModal from "@/components/reservations/ReservationActionsModal";
import SlotsDialog from "@/components/reservations/SlotsDialog";
import CreateReservationDialog from "@/components/reservations/CreateReservationDialog";
import EditReservationDialog from "@/components/reservations/EditReservationDialog";
import ReservationsList from "@/components/reservations/ReservationsList";
import { Calendar, Bell, Plus, Settings2, Search } from "lucide-react";


export default function ReservationsPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const [reservations, setReservations]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState("all");
  const [search, setSearch]               = useState("");
  const [editReservation, setEditReservation] = useState(null);
  const [showCreate, setShowCreate]       = useState(false);
  const [showSlots, setShowSlots]         = useState(false);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [newCount, setNewCount]           = useState(0);

  const prevCountRef = useRef(0);
  const audioRef     = useRef(null);

  const fetchReservations = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/reservations");
      const data = res.data;
      const pendingCount = data.filter(r => r.status === "pending").length;
      if (pendingCount > prevCountRef.current && prevCountRef.current > 0) {
        playNotification();
        toast.info("Nova reserva recebida!");
      }
      prevCountRef.current = pendingCount;
      setNewCount(pendingCount);
      setReservations(data);
    } catch { toast.error("Erro ao carregar reservas"); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReservations();
    const interval = setInterval(fetchReservations, 10000);
    return () => clearInterval(interval);
  }, [fetchReservations]);

  const playNotification = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgj7K3nWc+MFqFrraxeEo3WIWtt7F+TztYha23sXtLOFiHr7ixfEw5WIevuLF9TDpYh6+4sXxMOViHr7ixfEw5");
      }
      audioRef.current.play().catch(() => {});
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await authAxios.patch(`/reservations/${id}`, { status });
      toast.success(status === "confirmed" ? "Reserva confirmada ✅" : "Reserva cancelada");
      fetchReservations();
    } catch { toast.error("Erro ao actualizar"); }
  };

  const filtered = reservations.filter(r => {
    if (filter === "pending")   return r.status === "pending";
    if (filter === "confirmed") return r.status === "confirmed";
    if (filter === "cancelled") return r.status === "cancelled";
    if (filter === "today")     return r.date === new Date().toISOString().split("T")[0];
    return true;
  }).filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.customer_name || "").toLowerCase().includes(q) || (r.customer_phone || "").includes(q);
  });

  const stats = {
    total:     reservations.length,
    pending:   reservations.filter(r => r.status === "pending").length,
    confirmed: reservations.filter(r => r.status === "confirmed").length,
    today:     reservations.filter(r => r.date === new Date().toISOString().split("T")[0]).length,
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="reservations-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reservas</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Gestão de reservas do restaurante</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {newCount > 0 && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                <Bell className="w-4 h-4 text-amber-600 animate-pulse" />
                <span className="text-sm font-semibold text-amber-700">{newCount} pendente{newCount > 1 ? "s" : ""}</span>
              </div>
            )}
            <Button variant="outline" onClick={() => setShowSlots(true)} className="gap-2 flex-1 sm:flex-none" size="sm" data-testid="slots-btn">
              <Settings2 className="w-4 h-4" /><span className="hidden sm:inline">Disponibilidade</span><span className="sm:hidden">Slots</span>
            </Button>
            <Button onClick={() => setShowCreate(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-1 sm:flex-none" size="sm" data-testid="add-reservation-btn">
              <Plus className="w-4 h-4" /><span className="hidden sm:inline">Adicionar Reserva</span><span className="sm:hidden">Adicionar</span>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 sm:mb-6">
          {[
            { label: "Total", value: stats.total, color: "bg-gray-50" },
            { label: "Pendentes", value: stats.pending, color: "bg-amber-50" },
            { label: "Confirmadas", value: stats.confirmed, color: "bg-emerald-50" },
            { label: "Hoje", value: stats.today, color: "bg-blue-50" },
          ].map(s => (
            <div key={s.label} className={`${s.color} rounded-xl p-3 sm:p-4 text-center`}>
              <p className="text-xl sm:text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-gray-500 uppercase">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
            {["all", "pending", "confirmed", "cancelled", "today"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition shrink-0 ${filter === f ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`filter-${f}`}>
                {f === "all" ? "Todas" : f === "pending" ? "Pendentes" : f === "confirmed" ? "Confirmadas" : f === "cancelled" ? "Canceladas" : "Hoje"}
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="pl-9 w-full sm:w-60" data-testid="search-reservations" />
          </div>
        </div>

        <ReservationsList
          reservations={filtered}
          loading={loading}
          onEdit={setEditReservation}
          onUpdateStatus={updateStatus}
          onOpenActions={(r) => { setSelectedReservation(r); setShowActionsModal(true); }}
        />
      </div>

      {/* Modals */}
      <SlotsDialog open={showSlots} onClose={() => setShowSlots(false)} authAxios={authAxios} onRefresh={fetchReservations} />
      <CreateReservationDialog open={showCreate} onClose={() => setShowCreate(false)} authAxios={authAxios} onRefresh={fetchReservations} />
      <EditReservationDialog open={!!editReservation} onClose={() => setEditReservation(null)} reservation={editReservation} authAxios={authAxios} onRefresh={fetchReservations} />
      <ReservationActionsModal
        open={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        reservation={selectedReservation}
        authAxios={authAxios}
        onActionComplete={fetchReservations}
      />

      <Toaster position="top-right" />
    </MainLayout>
  );
}
